import { useMarket } from '@ecosystem/market-context-selection';
import { useAnalytics, useAuthentication, useFeedback } from '@ecosystem/newron-client-lib';
import React, { Suspense, lazy, useCallback, useContext, useEffect, useRef } from 'react';

import { NotificationToastType } from '../../../common';
import { UpsellContext } from '../../../state';
import { setIsModalOpen, setNotificationMessage } from '../../../state/action';
import { waitForElement } from '../../../utils';

import '../consumer-stories-qualtrics-slide/ConsumerStoriesQualtricsSlide.css';
import './ConsumerStoriesRLSSlide.css';

import { getMessageCode } from './utils/getMessageCode';

const Image = lazy(() => import('./images/Image'));

const QualtricsFeedbackIdentifier = 'ConsumerSlidesQualtricsFeedbackIdentifier';
const QualtricsButtonsSelector = '.QSI__EmbeddedFeedbackContainer_TextButton';

export const ConsumerStoriesRLSQualtricsSlide = (): React.ReactElement => {
  const {
    dispatch,
    state: { isModalOpen, pageName, bannerNumber },
  } = useContext(UpsellContext);
  const { extendEmbeddedData } = useFeedback();
  const { user } = useAuthentication();
  const { market } = useMarket();
  const { track } = useAnalytics();
  const bannerId = useRef<number | undefined>(bannerNumber || undefined);

  useEffect(() => {
    if (bannerNumber !== bannerId.current) {
      bannerId.current = bannerNumber;
    }
  }, [bannerNumber]);

  const eventClick = useCallback(
    button => {
      if (button?.title === 'Yes, I am interested') {
        track('upsell-lead-generated', {
          pageName,
          bannerName: getMessageCode(bannerId.current),
        });
        dispatch(
          setNotificationMessage({
            message: 'Thank you for your request. We will be in touch with more information.',
            type: NotificationToastType.Success,
            timeout: 3000,
          })
        );
      } else {
        track('upsell-lead-not-generated', {
          pageName,
          bannerName: getMessageCode(bannerId.current),
        });
      }
      dispatch(setIsModalOpen(false));
    },
    // eslint-disable-next-line
        [dispatch, track]
  );

  const handleClick = useCallback(async () => {
    const elementExists = await waitForElement(QualtricsButtonsSelector);
    if (elementExists) {
      const buttons = document.querySelectorAll(QualtricsButtonsSelector);
      buttons.forEach((button: HTMLElement) => {
        button.onclick = () => {
          eventClick(button);
        };
      });
    }
  }, [eventClick]);

  useEffect(() => {
    if (isModalOpen) {
      extendEmbeddedData({
        market: `${market?.itemGroupId}/${market?.countryId}`,
        country: user?.country,
      });
      handleClick();
    }
  }, [extendEmbeddedData, handleClick, isModalOpen, market, user?.country]);

  return (
    <Suspense fallback={null}>
      <div
        className="consumer-stories-qualtrics-slide consumer-stories-retail-lost-shoppers-qualtrics-slide text-left pt-0 px-8 pb-6"
        data-test-id="consumer-stories-retail-lost-shoppers-qualtrics-slide"
      >
        <div className="consumer-stories-qualtrics-slide-content flex flex-column justify-center">
          <div className="consumer-stories-qualtrics-slide-content-wrapper consumer-stories-retail-lost-shoppers-qualtrics-slide-content-wrapper flex justify-center">
            <div className="consumer-stories-retail-lost-shoppers-qualtrics-slide-text-wrapper mt-0.5">
              <p className="text-2xl text-brand font-bold">
                Maximize future shopper conversion with gfknewron Consumer
              </p>
              <ul className="marked-list text-xl">
                <li className="pt-2 pb-2">Quantify size and value of lost shoppers</li>
                <li className="pt-2 pb-2">Uncover why shoppers buy elsewhere</li>
                <li className="pt-2 pb-2">Know how to target shoppers in future</li>
              </ul>
              <p className="pt-8 pb-2 text-xl">
                Register your interest today and your GfK Account manager will be in touch
              </p>
            </div>
            <Image />
          </div>
          <div
            className="consumer-stories-qualtrics-slide-buttons flex justify-evenly"
            id={QualtricsFeedbackIdentifier}
            data-test-id="qualtrics-buttons"
          />
          <p className="consumer-stories-qualtrics-slide-contact text-sm mb-4 mt-4 text-center">
            You can opt-out from receiving further communication on this by contacting us. Please refer to{' '}
            <a href="/privacy-policy" target="_blank">
              Privacy Policy
            </a>{' '}
            for more information.
          </p>
        </div>
      </div>
    </Suspense>
  );
};
